import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

const decodeJWT = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('JWT decode error:', error);
    return null;
  }
};

interface UserInfo {
  user_id: number;
  username: string;
  email: string;
  role: number;
  comp_id?: number;
  exp?: number;
}

type UserContextType = {
  userInfo: UserInfo | null;
  isAuthenticated: boolean;
  refreshUserInfo: () => void;
  logout: () => void;
};

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);

  const refreshUserInfo = () => {
    const token = localStorage.getItem('token');
    
    if (token) {
      const decoded = decodeJWT(token);
      
      if (decoded) {
        const currentTime = Date.now() / 1000;
        
        if (decoded.exp && decoded.exp < currentTime) {
          logout();
          return;
        }
        
        const userInfoData: UserInfo = {
          user_id: decoded.userID,
          username: decoded.userName,
          email: decoded.email,
          role: decoded.role,
          comp_id: decoded.comp_id,
          exp: decoded.exp,
        };
        
        setUserInfo(userInfoData);
      } else {
        setUserInfo(null);
      }
    } else {
      setUserInfo(null);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('selectedCompany');
    setUserInfo(null);
    import('../configs/interceptors/interceptors').then(({ setAuthToken }) => {
      setAuthToken(null);
    });
  };

  useEffect(() => {
    refreshUserInfo();
    
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'token') {
        refreshUserInfo();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const value = {
    userInfo,
    isAuthenticated: !!userInfo,
    refreshUserInfo,
    logout,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}; 